import type { ReactNode } from 'react'
import EmptyState from '../../components/shell/EmptyState'
import ColumnFilter, { type ColumnFilterConfig } from './ColumnFilter'
import type { ReportSort } from './reportSort'

export type ReportTableColumn<Row> = {
  id: string
  label: string
  numeric?: boolean
  // An arrow in the heading that filters the table by this column's values.
  filter?: ColumnFilterConfig
  cell: (row: Row) => ReactNode
}

const HEADER_CLASS = 'px-4 py-2.5 text-caption font-bold tracking-wide text-ink-subtle uppercase'
const CELL_CLASS = 'px-4 py-2.5 text-small [font-variant-numeric:tabular-nums]'

/**
 * A sortable report table in the keyboard-reachable scroll region the style
 * guide asks for. The first column heads each row. The report sorts its own
 * rows; this draws them and reports a click on a heading.
 */
export default function ReportTable<Row>({
  label,
  caption,
  columns,
  rows,
  rowKey,
  sort,
  onSort,
  onClearFilters,
  loading,
  empty,
  minWidth = 900,
}: {
  label: string
  caption: string
  columns: ReportTableColumn<Row>[]
  rows: Row[]
  rowKey: (row: Row) => string
  sort: ReportSort
  onSort: (column: string) => void
  // Offered above the table while any column is filtered.
  onClearFilters?: () => void
  loading: boolean
  empty: string
  minWidth?: number
}) {
  const filtered = columns.some((column) => column.filter?.selected !== undefined)
  return (
    <div role="region" aria-label={label} tabIndex={0} className="overflow-x-auto rounded-card border border-line bg-canvas">
      {filtered && onClearFilters && (
        <div className="flex justify-end border-b border-line px-4 py-1">
          <button type="button" onClick={onClearFilters} className="min-h-11 rounded-control px-3 text-small font-bold text-primary hover:bg-surface-2">
            Clear filters
          </button>
        </div>
      )}
      {loading ? (
        <div aria-hidden="true" className="flex flex-col gap-2 p-4">
          {[0, 1, 2, 3].map((line) => (
            <div key={line} className="h-8 animate-pulse rounded-control bg-surface-2" />
          ))}
        </div>
      ) : rows.length === 0 && !filtered ? (
        <EmptyState>{empty}</EmptyState>
      ) : (
        <table className="w-full border-collapse" style={{ minWidth }}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-line bg-surface-2">
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={sort.column === column.id ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className={`${HEADER_CLASS} ${column.numeric ? 'text-right' : 'text-left'}`}
                >
                  <button type="button" onClick={() => onSort(column.id)} className="min-h-11 font-bold tracking-wide uppercase">
                    {column.label}
                  </button>
                  {column.filter && <ColumnFilter label={column.label} {...column.filter} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-small text-ink-muted">
                  {empty}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={rowKey(row)} className="border-b border-line last:border-b-0">
                {columns.map((column, index) =>
                  index === 0 ? (
                    <th key={column.id} scope="row" className={`${CELL_CLASS} font-semibold ${column.numeric ? 'text-right' : 'text-left'}`}>
                      {column.cell(row)}
                    </th>
                  ) : (
                    <td key={column.id} className={`${CELL_CLASS} ${column.numeric ? 'text-right' : ''}`}>
                      {column.cell(row)}
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
